import { constants as config } from '@config';

import historyTools from './history-tools';

const Driver = require('./dbDriver');

let undoHistory;
let isEnabled = false;
let isWorking = false;
const processors = {
  default: require('./processors/default'),
  'design.sticker.create': require('./processors/sticker-create'),
  'design.image.update': require('./processors/image-update'),
  'design.logo.update': require('./processors/logo-update'),
  'design.icon.update': require('./processors/logo-update'),
  'design.text.update': require('./processors/text-update'),
  resize: require('./processors/crop-exception')
};

function History () { }
History.prototype = _.extend({}, Backbone.Events);

History.prototype.initialize = function (data) {
  if (this.initialized || this.initializing) return;
  this.initializing = true;
  this.editor = INKIVE.Tool.getInstance().editor;
  this.canvas = this.editor.canvas;

  undoHistory = {};

  _.bindAll(this,
    'undo',
    'redo',
    'reload',
    'onChange',
    'processVersion',
    '_onError',
    '_addVersionHistory',
    '_triggerDefaultEvent'
  );

  this.driver = new Driver();
  this.driver.initialize().openDB().then(_.bind(function () {
    const isLastDBVersion = this.driver.isLastDBVersion();

    isLastDBVersion && this.driver.cleanOldVersions()
      .then(_.bind(storeInitialVersion, this));

    isLastDBVersion || this.driver.deleteDB()
      .then(this.driver.openDB)
      .then(_.bind(storeInitialVersion, this));
  }, this));

  function storeInitialVersion () {
    // TODO: load session history
    this.initialized = true;
    this._pushLocalVersion(data);
  }
};

History.prototype._triggerDefaultEvent = function (eventName) {
  _.size(undoHistory) && this.trigger(eventName, {
    current: this.currentVersion,
    history: undoHistory,
    isEnabled
  });
};

History.prototype.processVersion = function (data) {
  const $workarea = $('#workarea');
  const scrollTop = $workarea.scrollTop();
  const scrollLeft = $workarea.scrollLeft();
  const targetSvgString = INKIVE.Helpers.imagesSanitize.base64Load(data.svg);

  isWorking = false;

  // Keep a list of selected elements (to restore later)
  const selectedElements = INKIVE.EditorFacade.canvas.getSelectedElems();

  // Rollback in select mode only
  if (INKIVE.EditorFacade.canvas.getMode() === 'table-edit') {
    INKIVE.Table.endTableEditingMode();
  }
  INKIVE.EditorFacade.canvas.setMode('select');

  // The desired Dom
  const targetDOM = historyTools.stringToDom(targetSvgString);
  const canvasSize = getCanvasSize(targetDOM);
  if (shouldUpdateSize(canvasSize)) {
    INKIVE.DesignFacade.set('template', { size: canvasSize });
  }
  // Get the differences between this snapshot and the current state
  const changePaths = Object.keys(historyTools.compareHistorySnapshots(targetDOM));
  // An array of differences {changePath:string; depth:number}
  const differences = changePaths
    .reduce((ac, changePath) => {
      const depth = changePath.split('/').length;
      return [...ac, { changePath, depth }];
    }, [])
    .sort((a, b) => a.depth - b.depth); // Sort so parents get processed before children

  // Rollback (undo/redo) changes
  for (let i = 0; i < differences.length; i++) {
    // Don't process a child if its parent is already processed
    // as processing an element also processes its all children
    let parentFound = false;
    for (let j = 0; j < i; j++) {
      if (differences[i].changePath.startsWith(differences[j].changePath)) {
        parentFound = true;
        break;
      }
    }

    parentFound || historyTools.rollbackChange(targetDOM, differences[i].changePath);
  }

  $workarea.scrollLeft(scrollLeft);
  $workarea.scrollTop(scrollTop);

  // Reset selection transform
  svgedit.transformlist.resetListMap();

  // Update images with placeholders (undo/redo may remove their href)
  const mainGroup = document.querySelector('#svgcontent g.main');
  const svgRoot = document.querySelector('#svgroot');

  INKIVE.Helpers.imagesSanitize.postLoadImagesSanitize(mainGroup);
  INKIVE.Helpers.imagesSanitize.postLoadBackgroundSanitize(svgRoot);

  INKIVE.Text._cache = {};
  INKIVE.Text.Helpers.hiddenContent = data.hiddenContent;

  const targetTextMock = data.textMock && historyTools.stringToDom(INKIVE.Helpers.imagesSanitize.base64Load(data.textMock));
  targetTextMock && historyTools.rollbackTextMockChange(targetTextMock);

  const visibleElements = INKIVE.EditorFacade.canvas.getVisibleElements();
  const filteredSelectedElements = selectedElements.filter(elem => visibleElements.includes(elem));

  if (filteredSelectedElements.length) {
    filteredSelectedElements
      .forEach(elem => this.canvas.selectorManager.requestSelector(elem).resize());
  } else {
    this.canvas.selectorManager.releaseAllSelectors();
  }
  this._triggerDefaultEvent('update');

  function getCanvasSize (targetDOM) {
    const canvas = targetDOM.querySelector('svg');
    const width = Number(canvas.getAttribute('width'));
    const height = Number(canvas.getAttribute('height'));
    const inlineSize = INKIVE.Helpers.getInlineSize(canvas);

    if (inlineSize.width && inlineSize.height && inlineSize.unit) {
      const dpi = getMatchedDpi(inlineSize, { width, height });

      return { ...inlineSize, dpi };
    }

    if (isNaN(width) || isNaN(height)) return;

    return {
      width: Number(canvas.getAttribute('width')),
      height: Number(canvas.getAttribute('height'))
    };
  }

  function shouldUpdateSize (size) {
    if (!size) return false;

    const canvas = {
      width: INKIVE.EditorFacade.canvas.contentW,
      height: INKIVE.EditorFacade.canvas.contentH
    };

    return !isSameSize(size, canvas);
  }

  function getMatchedDpi (size, canvas) {
    return config.dpi.variations.find(dpi => {
      return isMatchedDpi({ ...size, dpi }, canvas);
    });
  }

  function isMatchedDpi (size, target, threshold = 0.1) {
    return ['width', 'height']
      .every(attribute =>
        Math.abs(
          1 - (INKIVE.Helpers.convertUnitsToPixels(size[attribute], size.unit, false, size.dpi) / target[attribute])
        ) < threshold
      );
  }

  function isSameSize (size, target, threshold = 5) {
    return ['width', 'height']
      .every(attribute =>
        Math.abs(
          INKIVE.Helpers.convertUnitsToPixels(size[attribute], size.unit, false, size.dpi) - target[attribute]
        ) < threshold
      );
  }
};

History.prototype.reload = function (data) {
  if (!this.initialized) return;

  undoHistory = {};
  this._loadStoredVersions(data).then(_.bind(function () {
    if (!_.size(undoHistory)) {
      this.currentVersion = undefined;
      this._pushLocalVersion(data).then(
        _.partial(this._triggerDefaultEvent, 'reload')
      );
    } else {
      this._triggerDefaultEvent('reload');
    }
  }, this));
};

History.prototype.onChange = function (eventName, eventData, data) {
  if (!isEnabled || !this.initialized) return;
  try {
    eventData = eventData || {};
    INKIVE.hasEditorUnsavedChanges = !eventData.is_initial_load;

    const p = processors[eventName] ? eventName : 'default';
    processors[p].call(this, eventName, eventData, data).then(
      _.partial(this._triggerDefaultEvent, 'change'), $.noop
    );
  } catch (e) {
    console.error(e);
    _.defer(this._onError);
  }
};

History.prototype.getVersion = function (id) {
  this.driver.getVersion(id).then(_.bind(function (data) {
    const svg = data.svg.replace(/"/g, '\\"').replace(/\n/g, ' ');
    this.trigger(id, svg);
  }, this));
  return this.driver.getVersion(id);
};

History.prototype.getCurrentId = function () {
  return this.currentVersion;
};

History.prototype.undo = function () {
  const canUndo = isEnabled && !isWorking &&
    this.currentVersion &&
    undoHistory[this.currentVersion] &&
    undoHistory[this.currentVersion].parent;

  if (!canUndo) return;

  isWorking = true;
  this.currentVersion = undoHistory[this.currentVersion].parent;
  this.driver.getVersion(this.currentVersion)
    .then(response => {
      this.processVersion(response);
      this._triggerDefaultEvent('undo');
    }, this._onError);
};

History.prototype.redo = function (version) {
  const canRedo = isEnabled && !isWorking &&
    this.currentVersion &&
    undoHistory[this.currentVersion] &&
    undoHistory[this.currentVersion].children.length;

  if (!canRedo) return;

  isWorking = true;
  // TODO: branches
  this.currentVersion = undoHistory[this.currentVersion].children[0];
  this.driver.getVersion(this.currentVersion)
    .then(response => {
      this.processVersion(response);
      this._triggerDefaultEvent('redo');
    }, this._onError);
};

History.prototype.restore = function (string) {
  this.processVersion(string);
  this._pushLocalVersion(string);

  INKIVE.hasEditorUnsavedChanges = true;
};

History.prototype._pushLocalVersion = function (data) {
  const deferred = $.Deferred();
  const currentVersion = this.currentVersion;

  this.driver.storeVersion({
    svg: data.svg && INKIVE.Helpers.imagesSanitize.base64Sanitize(data.svg),
    textMock: data.textMock && INKIVE.Helpers.imagesSanitize.base64Sanitize(data.textMock),
    hiddenContent: data.hiddenContent,
    timestamp: new Date(),
    parent: currentVersion,
    project: INKIVE.C.BOOK.encodedId,
    page: INKIVE.Tool._current_page_number
  }).then(_.bind(function (nextVersion) {
    this._addVersionHistory(nextVersion, currentVersion);
    deferred.resolve();
  }, this));

  return deferred.promise();
};

History.prototype._loadStoredVersions = function (data) {
  const deferred = $.Deferred();
  const storedHistory = {};

  this.currentVersion = -1;
  this.driver.iterateCurrentPageVersions(_.bind(function (cursor) {
    if (cursor) {
      addVersion.call(this, cursor.value);
      cursor.continue();
    } else setCurrentVersion.call(this);
  }, this));

  return deferred.promise();

  function addVersion (v) {
    storedHistory[v.version] = {
      version: v.version,
      parent: v.parent,
      children: []
    };

    if (this.currentVersion < v.version && INKIVE.Helpers.areEqual(data, v.svg)) {
      this.currentVersion = v.version;
    }
  }

  function setCurrentVersion () {
    if (this.currentVersion > -1) {
      undoHistory = storedHistory;

      // TODO: branches support
      _.each(undoHistory, function (v) {
        if (typeof v.parent !== 'undefined' && undoHistory[v.parent]) {
          undoHistory[v.parent].children = [v.version];
        }
      }, this);
    }

    deferred.resolve();
  }
};

History.prototype._addVersionHistory = function (nextVersion, currentVersion) {
  if (typeof currentVersion !== 'undefined') {
    if (!undoHistory[currentVersion]) {
      console.warn('Current version not found');
      return;
    }

    // TODO: support branches
    // undoHistory[currentVersion].children.push(nextVersion);
    undoHistory[currentVersion].children = [nextVersion];
  }

  undoHistory[nextVersion] = {
    version: nextVersion,
    parent: currentVersion,
    children: []
  };

  this.currentVersion = nextVersion;
};

History.prototype._onError = function (nextVersion) {
  isEnabled = false;
  isWorking = false;
  this._triggerDefaultEvent('error');
  this.driver.deleteDB();
};

History.prototype.isSystemEnabled = function () {
  return isEnabled;
};

History.prototype.enableSystem = function () {
  isEnabled = true;
};

module.exports = new History();
